import { Banner } from '@cloudflare/kumo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { inboxApi } from '../services/api';
import { InboxWebSocket, inboxWsEnabled } from '../services/inboxWs';
import { ConversationList } from '../components/Inbox/ConversationList';
import { intentFilterParams } from '../components/Inbox/intents';
import { ConversationDetail } from '../components/Inbox/ConversationDetail';
import './InboxPage.css';

type ConversationRow = {
  id: string;
  channel: 'whatsapp' | 'instagram' | 'widget';
  status: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED';
  lastMessageAt: string;
  lastPreview: string | null;
  unread: number;
  contact: { id: string; nome: string; telefone: string | null; tags: string[] } | null;
  assignee: { id: string; nome: string } | null;
  last: { content: string } | null;
  intent?: string | null;
  intentConfidence?: number | null;
  urgent?: boolean;
};
type ConversationDetailData = {
  conversation: ConversationRow & {
    summary: string | null;
    summaryGeneratedAt: string | null;
    summaryModel: string | null;
    lastInboundAt: string | null;
  };
  contact: { id: string; nome: string; telefone: string | null; email: string | null; tags: string[] } | null;
  assignee: { id: string; nome: string } | null;
  window: { open: boolean; closesAt: string | null };
  messages: Array<{
    id: string;
    kind: 'INBOUND' | 'OUTBOUND' | 'NOTE' | 'SYSTEM' | 'AI';
    direction: 'IN' | 'OUT';
    content: string;
    mediaType: string | null;
    mediaUrl: string | null;
    authorId: string | null;
    author: string | null;
    createdAt: string;
  }>;
};

const POLL_MS = 30_000;

type ApiMessage = { id: string; role: string; kind: string; content: string; mediaUrl: string | null; authorId: string | null; author: string | null; createdAt: string };
const MEDIA = new Set(['image', 'audio', 'video', 'document']);

/**
 * O backend guarda quem falou em `role` (contact | ai | agent | bot | note) e o
 * tipo de conteúdo em `kind` (text | image | audio | video | document | note |
 * campaign). A timeline trabalha com origem (INBOUND/OUTBOUND/AI/NOTE) e direção.
 */
function toTimeline(m: ApiMessage): ConversationDetailData['messages'][number] {
  const note = m.role === 'note' || m.kind === 'note';
  const kind = note ? 'NOTE' : m.role === 'contact' ? 'INBOUND' : m.role === 'ai' ? 'AI' : 'OUTBOUND';
  const author = m.author ?? (kind === 'AI' ? 'Assistente (IA)' : m.role === 'bot' || m.kind === 'campaign' ? 'Automação' : null);
  return {
    id: m.id, kind, direction: kind === 'INBOUND' ? 'IN' : 'OUT', content: m.content,
    mediaType: MEDIA.has(m.kind) ? m.kind : null, mediaUrl: m.mediaUrl, authorId: m.authorId, author, createdAt: m.createdAt,
  };
}

const errMsg = (e: unknown, fallback: string) =>
  e instanceof Error ? e.message : fallback;

/**
 * Caixa de entrada omnichannel (issue #158).
 * Layout split: lista à esquerda, detalhe à direita; em mobile, detalhe ocupa a
 * tela cheia com botão de voltar. Polling simples a cada 30 s.
 */
export function InboxPage() {
  const { user } = useAuth();
  const [view, setView] = useState('open');
  const [channel, setChannel] = useState('');
  const [intent, setIntent] = useState('');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [list, setList] = useState<ConversationRow[]>([]);
  const [counts, setCounts] = useState({ mine: 0, queue: 0, open: 0 });
  const [listLoading, setListLoading] = useState(false);
  const [detail, setDetail] = useState<ConversationDetailData | null>(null);
  const [agents, setAgents] = useState<Array<{ id: string; nome: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  // debounce de busca
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(q), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setError(null);
    try {
      const data = await inboxApi.list({
        view,
        channel: channel || undefined,
        q: debouncedQ || undefined,
        ...intentFilterParams(intent),
      });
      setList(data.conversations as ConversationRow[]);
      setCounts(data.counts);
    } catch (e) {
      setError(errMsg(e, 'Falha ao listar conversas'));
    } finally {
      setListLoading(false);
    }
  }, [view, channel, intent, debouncedQ]);

  const fetchAgents = useCallback(async () => {
    try {
      const data = await inboxApi.agents();
      setAgents(data.agents);
    } catch { /* silencioso: atribuição fica sem opções */ }
  }, []);

  const fetchDetail = useCallback(async (id: string, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setBusy(true);
    setError(null);
    try {
      const data = (await inboxApi.get(id)) as unknown as Omit<ConversationDetailData, 'messages'> & { messages: ApiMessage[] };
      setDetail({ ...data, messages: data.messages.map(toTimeline) });
    } catch (e) {
      setError(errMsg(e, 'Falha ao carregar conversa'));
    } finally {
      if (!silent) setBusy(false);
    }
  }, []);

  // Carrega lista quando filtros mudam
  useEffect(() => { fetchList(); }, [fetchList]);

  // Polling 30s para lista
  useEffect(() => {
    const t = setInterval(() => { fetchList(); }, POLL_MS);
    return () => clearInterval(t);
  }, [fetchList]);

  // Carrega agentes uma vez
  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // Shadow traffic Inbox WS via DO (issue #209): opt-in por feature flag. Quando
  // ativa, escuta inbox:message e inbox:conversation vindos do Durable Object e
  // revalida lista/detalhe. socket.io segue rodando em paralelo (no-op aqui:
  // o hook useNotifications fica responsável por ele).
  useEffect(() => {
    if (!inboxWsEnabled()) return;
    const tenantId = user?.tenantId;
    if (!tenantId) return;
    const ws = new InboxWebSocket({ tenantId });
    const off = ws.on((payload) => {
      if (payload.event === 'inbox:message') {
        fetchList();
        if (selectedId) fetchDetail(selectedId, { silent: true });
      } else if (payload.event === 'inbox:conversation') {
        fetchList();
        if (selectedId) fetchDetail(selectedId, { silent: true });
      }
    });
    ws.connect();
    return () => { off(); ws.close(); };
    // re-apenas quando muda o tenant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenantId]);

  // Carrega detalhe (e mantém sincronizado) quando muda a conversa
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    fetchDetail(selectedId);
    const t = setInterval(() => { fetchDetail(selectedId, { silent: true }); }, POLL_MS);
    return () => clearInterval(t);
  }, [selectedId, fetchDetail]);

  const selectConversation = (id: string) => {
    setSelectedId(id);
    setMobileShowDetail(true);
  };
  const backToList = () => setMobileShowDetail(false);

  const reply = async (text: string) => {
    if (!selectedId) return;
    await inboxApi.reply(selectedId, text);
    await fetchDetail(selectedId, { silent: true });
    fetchList();
  };

  const addNote = async (text: string) => {
    if (!selectedId) return;
    await inboxApi.addNote(selectedId, text);
    await fetchDetail(selectedId, { silent: true });
  };

  const claim = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await inboxApi.claim(selectedId);
      await fetchDetail(selectedId, { silent: true });
      fetchList();
    } catch (e) {
      setError(errMsg(e, 'Não foi possível assumir a conversa'));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (userId: string | null) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await inboxApi.assign(selectedId, userId);
      await fetchDetail(selectedId, { silent: true });
      fetchList();
    } catch (e) {
      setError(errMsg(e, 'Falha ao atribuir'));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (s: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED') => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await inboxApi.setStatus(selectedId, s);
      await fetchDetail(selectedId, { silent: true });
      fetchList();
    } catch (e) {
      setError(errMsg(e, 'Falha ao mudar status'));
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    if (!selectedId) return;
    try {
      const data = await inboxApi.suggest(selectedId);
      const input = document.querySelector<HTMLTextAreaElement>('[data-testid="inbox-reply"] textarea');
      if (input && data.text) {
        // Dispara evento para o React detectar
        const native = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
        native?.call(input, data.text);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      }
    } catch (e) {
      setError(errMsg(e, 'Falha ao sugerir resposta'));
    }
  };

  const summarize = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const data = await inboxApi.summary(selectedId);
      if (detail) {
        setDetail({
          ...detail,
          conversation: {
            ...detail.conversation,
            summary: data.summary,
            summaryModel: data.model,
            summaryGeneratedAt: new Date().toISOString(),
          },
        });
      }
    } catch (e) {
      setError(errMsg(e, 'Falha ao resumir'));
    } finally {
      setBusy(false);
    }
  };

  const currentUserId = user?.id ?? '';

  return (
    <div className="inbox-page" data-testid="inbox-page">
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Caixa de entrada</h2>
        <div className="text-kumo-subtle">
          Conversas do WhatsApp, Instagram e do widget do site num só lugar. A IA atende e a equipe assume quando precisa.
        </div>
      </div>

      <div className="page-content inbox-page__body">
      {error && (
        <Banner variant="error" className="inbox-page__error" data-testid="inbox-error" description={<>
          {error}
        </>} />
      )}

      <div className={`inbox-page__layout ${mobileShowDetail ? 'inbox-page__layout--show-detail' : ''}`}>
        <ConversationList
          conversations={list}
          selectedId={selectedId}
          onSelect={selectConversation}
          counts={counts}
          view={view}
          onViewChange={setView}
          channel={channel}
          onChannelChange={setChannel}
          intent={intent}
          onIntentChange={setIntent}
          q={q}
          onQueryChange={setQ}
          loading={listLoading}
        />

        {/* O anúncio de mensagens novas (aria-live) fica na timeline, dentro do detalhe */}
        <div className="inbox-page__detail-pane">
          {detail ? (
            <ConversationDetail
              conversation={detail.conversation}
              contact={detail.contact}
              assignee={detail.assignee}
              messages={detail.messages}
              windowInfo={detail.window}
              agents={agents}
              currentUserId={currentUserId}
              busy={busy}
              onReply={reply}
              onAddNote={addNote}
              onClaim={claim}
              onAssign={assign}
              onSetStatus={setStatus}
              onSuggest={suggest}
              onSummarize={summarize}
              onBack={backToList}
            />
          ) : (
            <div className="inbox-page__detail-empty" data-testid="inbox-empty">
              <div>Selecione uma conversa para abrir.</div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default InboxPage;
